/**
 * Redux Slice: Tab Registry Management
 * Operations: CRUD for tab registry with pattern assignments
 */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiRequest } from '../../../utils/apiService';
import type { RootState } from '../../store';

// Types
export interface TabRegistryItem {
    tab_id: number;
    tab_external_id: string;
    tab_label: string;
    tab_description?: string;
    tab_strategic_purpose?: string;
    tab_use_id: number;
    is_core_e_r_p?: number;
    record_type_id?: number;
    pattern_name?: string;
    default_tab_type_id?: number;
    default_ui_component_type?: string;
    default_manage_types?: string;
    ui_pattern_description?: string;
    binding_id?: number;
    controller_sp_name?: string;
    controller_sp_schema?: string;
    manage_types_supported?: string;
    is_unassigned_tab_use: number;
    is_missing_db_binding: number;
    is_missing_strategic_purpose: number;
    tab_created_at: string;
    tab_updated_at: string;
}

export interface TabUseOption {
    tab_use_id: number;
    record_type_id: number;
    pattern_name: string;
    default_tab_type_id: number;
    default_ui_component_type: string;
    default_manage_types: string;
    Natural_Description: string;
    is_active: number;
    created_at: string;
}

export interface TabRegistryStats {
    total_tabs: number;
    assigned_patterns: number;
    Unassigned: number;
    missing_db_binding: number;
    missing_strategic_purpose: number;
}

interface TabRegistryState {
    items: TabRegistryItem[];
    tabUseOptions: TabUseOption[];
    stats: TabRegistryStats | null;
    selectedTab: TabRegistryItem | null;
    loading: boolean;
    error: string | null;
    filters: {
        patternName: string | null;
        componentType: string | null;
        unassignedOnly: boolean;
        missingBindingOnly: boolean;
        missingPurposeOnly: boolean;
        searchText: string | null;
    };
}

const initialState: TabRegistryState = {
    items: [],
    tabUseOptions: [],
    stats: null,
    selectedTab: null,
    loading: false,
    error: null,
    filters: {
        patternName: null,
        componentType: null,
        unassignedOnly: false,
        missingBindingOnly: false,
        missingPurposeOnly: false,
        searchText: null,
    },
};

// Async Thunks
export const fetchTabRegistry = createAsyncThunk(
    'tabRegistry/fetchAll',
    async (filters: Partial<TabRegistryState['filters']> = {}, { rejectWithValue }) => {
        try {
            const result = await apiRequest<{ data: TabRegistryItem[] }>(
                '/api/tab-registry',
                'POST',
                {
                    manage: 4,
                    pattern_name: filters.patternName,
                    component_type: filters.componentType,
                    unassigned_only: filters.unassignedOnly,
                    missing_binding_only: filters.missingBindingOnly,
                    missing_purpose_only: filters.missingPurposeOnly,
                    search_text: filters.searchText,
                },
            );
            return result.data;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    },
);

export const fetchTabUseOptions = createAsyncThunk(
    'tabRegistry/fetchTabUseOptions',
    async (_, { rejectWithValue }) => {
        try {
            const result = await apiRequest<{ data: TabUseOption[] }>('/api/tab-registry', 'POST', {
                manage: 6,
            });
            return result.data;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    },
);

export const fetchTabRegistryStats = createAsyncThunk(
    'tabRegistry/fetchStats',
    async (_, { rejectWithValue }) => {
        try {
            const result = await apiRequest<{ stats: TabRegistryStats }>(
                '/api/tab-registry',
                'POST',
                { manage: 7 },
            );
            return result.stats;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    },
);

export const createTab = createAsyncThunk(
    'tabRegistry/create',
    async (tabData: Partial<TabRegistryItem>, { rejectWithValue }) => {
        try {
            const result = await apiRequest<{ data: TabRegistryItem[] }>(
                '/api/tab-registry',
                'POST',
                {
                    manage: 1,
                    tab_label: tabData.tab_label,
                    tab_description: tabData.tab_description,
                    tab_strategic_purpose: tabData.tab_strategic_purpose,
                    tab_use_id: tabData.tab_use_id,
                },
            );
            return result.data[0];
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    },
);

export const updateTab = createAsyncThunk(
    'tabRegistry/update',
    async (tabData: Partial<TabRegistryItem>, { rejectWithValue }) => {
        try {
            const result = await apiRequest<{ data: TabRegistryItem[] }>(
                '/api/tab-registry',
                'POST',
                {
                    manage: 2,
                    tab_id: tabData.tab_id,
                    tab_label: tabData.tab_label,
                    tab_description: tabData.tab_description,
                    tab_strategic_purpose: tabData.tab_strategic_purpose,
                    tab_use_id: tabData.tab_use_id,
                },
            );
            return result.data[0];
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    },
);

export const deleteTab = createAsyncThunk(
    'tabRegistry/delete',
    async (tabId: number, { rejectWithValue }) => {
        try {
            await apiRequest('/api/tab-registry', 'POST', { manage: 3, tab_id: tabId });
            return tabId;
        } catch (error: any) {
            return rejectWithValue(error.message);
        }
    },
);

// Slice
const tabRegistrySlice = createSlice({
    name: 'tabRegistry',
    initialState,
    reducers: {
        setFilters: (state, action) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        clearFilters: (state) => {
            state.filters = initialState.filters;
        },
        setSelectedTab: (state, action) => {
            state.selectedTab = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch All
            .addCase(fetchTabRegistry.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchTabRegistry.fulfilled, (state, action) => {
                state.loading = false;
                state.items = action.payload;
            })
            .addCase(fetchTabRegistry.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            // Fetch Tab Use Options
            .addCase(fetchTabUseOptions.fulfilled, (state, action) => {
                state.tabUseOptions = action.payload;
            })
            // Fetch Stats
            .addCase(fetchTabRegistryStats.fulfilled, (state, action) => {
                state.stats = action.payload;
            })
            // Create
            .addCase(createTab.fulfilled, (state, action) => {
                state.items.push(action.payload);
            })
            // Update
            .addCase(updateTab.fulfilled, (state, action) => {
                const index = state.items.findIndex(
                    (item) => item.tab_id === action.payload.tab_id,
                );
                if (index !== -1) {
                    state.items[index] = action.payload;
                }
            })
            // Delete
            .addCase(deleteTab.fulfilled, (state, action) => {
                state.items = state.items.filter((item) => item.tab_id !== action.payload);
            });
    },
});

export const { setFilters, clearFilters, setSelectedTab, clearError } = tabRegistrySlice.actions;

// Selectors
export const selectTabRegistryItems = (state: RootState) => state.tabRegistry.items;
export const selectTabUseOptions = (state: RootState) => state.tabRegistry.tabUseOptions;
export const selectTabRegistryStats = (state: RootState) => state.tabRegistry.stats;
export const selectTabRegistryLoading = (state: RootState) => state.tabRegistry.loading;
export const selectTabRegistryError = (state: RootState) => state.tabRegistry.error;
export const selectTabRegistryFilters = (state: RootState) => state.tabRegistry.filters;

export default tabRegistrySlice.reducer;
